import axios from 'axios';
import { apiClient, idempotentConfig, unwrap } from './client';
import type { ApiEnvelope } from './types';

/**
 * P3 — the screen studio (`/v1/screens**`, OPSFL-CLIENT-PLATFORM FSD §6). Draft → validate →
 * publish for the customer-facing journey SCREENS a flow's tasks render as. STAFF plane only: the
 * room never sees this API — it receives resolved strings through the existing task shapes. Keys:
 * `screens.read` (list/get), `screens.author` (validate/draft), `screens.publish` (a step-up lane
 * once enforcement flips). Mutations carry `ol-idempotency-key`.
 */

/** One compile diagnostic, JSON-pointer-addressed so the studio can focus the offending node. */
export interface ScreenDiagnostic {
  severity: 'ERROR' | 'WARNING';
  code: string;
  path: string;
  message: string;
}

export interface ScreenValidation {
  ok: boolean;
  diagnostics: ScreenDiagnostic[];
}

export interface ScreenConfigSummary {
  configId: string;
  version: number;
  flowId: string | null;
  status: 'DRAFT' | 'PUBLISHED' | 'SUPERSEDED';
  contentHash: string | null;
  locales: string[];
  createdBy: string | null;
  createdAt: string | null;
  publishedBy: string | null;
  publishedAt: string | null;
}

export interface ScreenConfigView extends ScreenConfigSummary {
  /** The stored definition — RFC-8785 canonical JSON text (exactly the signed bytes). */
  definition: string;
  signatureAlgorithm: string | null;
  signatureKeyId: string | null;
  signature: string | null;
  publishNote: string | null;
}

/** The closed lists the studio offers. The server is their one home (OL-24). */
export interface ScreenVocabulary {
  kinds: { kind: string; hasSpec: boolean; uiSlots: string[] }[];
  evidenceKinds: string[];
  fieldTypes: string[];
  locales: string[];
  layoutSlots: string[];
  reservedFieldNames: string[];
  partyRoles: string[];
  captureModes: string[];
  otpChannels: string[];
  identifierSchemes: string[];
  bankModes: string[];
  progressStyles: string[];
  uploadMediaTypes: string[];
  limits: Record<string, number>;
  /** The closed page variants (OL-23), by page. */
  pageVariants: Record<string, string[]>;
}

export interface ScreenTemplateSummary {
  templateId: string;
  name: string;
  description: string;
  /** The flow template this journey fits; null when it fits any flow. */
  flowTemplateId: string | null;
  jurisdiction: string;
  kinds: string[];
}

/** The server's answer to a preview: the gate's verdict and, if it passes, what the room would serve. */
export interface ScreenPreview {
  validation: ScreenValidation;
  locale: string;
  tasks: unknown[] | null;
  journey: unknown | null;
  theme: unknown | null;
  reviewLabel: string | null;
}

let vocabularyOnce: Promise<ScreenVocabulary> | null = null;

/** GET /v1/screens/vocabulary. It changes only with a deploy, so one fetch serves the page's life. */
export function getScreenVocabulary(): Promise<ScreenVocabulary> {
  vocabularyOnce ??= apiClient
    .get<ApiEnvelope<ScreenVocabulary>>('/v1/screens/vocabulary')
    .then(({ data }) => unwrap(data))
    .catch((e) => {
      vocabularyOnce = null;
      throw e;
    });
  return vocabularyOnce;
}

/** GET /v1/screens/templates[?flowTemplateId=]. */
export async function listScreenTemplates(flowTemplateId?: string): Promise<ScreenTemplateSummary[]> {
  const { data } = await apiClient.get<ApiEnvelope<ScreenTemplateSummary[]>>('/v1/screens/templates', {
    params: flowTemplateId ? { flowTemplateId } : undefined,
  });
  const body = unwrap(data);
  return Array.isArray(body) ? body : [];
}

/** GET /v1/screens/templates/{id} — the journey to seed a new draft from. */
export async function getScreenTemplate(
  templateId: string,
): Promise<{ summary: ScreenTemplateSummary; definition: unknown }> {
  const { data } = await apiClient.get<
    ApiEnvelope<{ summary: ScreenTemplateSummary; definition: unknown }>
  >(`/v1/screens/templates/${encodeURIComponent(templateId)}`);
  return unwrap(data);
}

/**
 * POST /v1/screens/{configId}/preview — runs the gate and builds what the room would serve, in this
 * locale, with the server's own view builder. Stores nothing and sends nothing.
 */
export async function previewScreenConfig(
  configId: string,
  definition: unknown,
  locale: string,
): Promise<ScreenPreview> {
  const { data } = await apiClient.post<ApiEnvelope<ScreenPreview>>(
    `/v1/screens/${encodeURIComponent(configId)}/preview`,
    { definition, locale },
  );
  return unwrap(data);
}

/** GET /v1/screens/{configId}/versions/{version}/preview — the same, for a stored version. */
export async function previewScreenVersion(
  configId: string,
  version: number,
  locale: string,
): Promise<ScreenPreview> {
  const { data } = await apiClient.get<ApiEnvelope<ScreenPreview>>(
    `/v1/screens/${encodeURIComponent(configId)}/versions/${version}/preview`,
    { params: { locale } },
  );
  return unwrap(data);
}

/** GET /v1/screens — every version of every config in the hub (config_id, version DESC). */
export async function listScreenConfigs(): Promise<ScreenConfigSummary[]> {
  const { data } = await apiClient.get<ApiEnvelope<ScreenConfigSummary[]>>('/v1/screens');
  const body = unwrap(data);
  return Array.isArray(body) ? body : [];
}

/** GET /v1/screens/{configId}/versions/{version} — one version, definition included. */
export async function getScreenConfigVersion(
  configId: string,
  version: number,
): Promise<ScreenConfigView> {
  const { data } = await apiClient.get<ApiEnvelope<ScreenConfigView>>(
    `/v1/screens/${encodeURIComponent(configId)}/versions/${version}`,
  );
  return unwrap(data);
}

/**
 * POST /v1/screens/{configId}/validate — dry-run the compile gate; stores NOTHING. Diagnostics
 * come back complete (all of them, not the first) so the author fixes everything at once.
 */
export async function validateScreenConfig(
  configId: string,
  definition: unknown,
): Promise<ScreenValidation> {
  const { data } = await apiClient.post<ApiEnvelope<ScreenValidation>>(
    `/v1/screens/${encodeURIComponent(configId)}/validate`,
    { definition },
  );
  const body = unwrap(data);
  return { ok: !!body?.ok, diagnostics: Array.isArray(body?.diagnostics) ? body.diagnostics : [] };
}

/**
 * POST /v1/screens/{configId} — a NEW draft version (server assigns max+1). A 422
 * `SCREEN_CONFIG_INVALID` carries the enumerated diagnostics — nothing is stored on ERROR.
 */
export async function draftScreenConfig(
  configId: string,
  definition: unknown,
  flowId?: string,
): Promise<ScreenConfigView> {
  const { data } = await apiClient.post<ApiEnvelope<ScreenConfigView>>(
    `/v1/screens/${encodeURIComponent(configId)}`,
    { flowId: flowId || null, definition },
    idempotentConfig(),
  );
  return unwrap(data);
}

/**
 * POST /v1/screens/{configId}/versions/{version}/publish — re-validates against THIS hub's
 * content ladder, signs (Ed25519 via the flow-signer seam), content-hashes, freezes the row and
 * supersedes the previous live version. Idempotent on an already-PUBLISHED version.
 */
export async function publishScreenConfig(
  configId: string,
  version: number,
  note?: string,
): Promise<ScreenConfigView> {
  const { data } = await apiClient.post<ApiEnvelope<ScreenConfigView>>(
    `/v1/screens/${encodeURIComponent(configId)}/versions/${version}/publish`,
    { note: note || null },
    idempotentConfig(),
  );
  return unwrap(data);
}

/**
 * Pull the enumerated diagnostics off a 422 `SCREEN_CONFIG_INVALID` axios error (draft or publish
 * refused by the gate). Null when the failure is anything else — the caller falls back to the
 * normal error path.
 */
export function extractScreenDiagnostics(err: unknown): ScreenDiagnostic[] | null {
  if (!axios.isAxiosError(err) || err.response?.status !== 422) return null;
  const body = err.response.data as
    | { code?: string; diagnostics?: ScreenDiagnostic[] }
    | undefined;
  if (body?.code !== 'SCREEN_CONFIG_INVALID' || !Array.isArray(body.diagnostics)) return null;
  return body.diagnostics;
}
